import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useCartSync } from "@/hooks/useCartSync";
import { ShopifyCustomerProvider } from "@/contexts/ShopifyCustomerContext";
import ScrollToTop from "@/components/ScrollToTop";

// Eager load the homepage for best LCP
import Index from "./pages/Index";

// Lazy load other pages for code splitting
const NotFound = lazy(() => import("./pages/NotFound"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Collection = lazy(() => import("./pages/Collection"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectArticle = lazy(() => import("./pages/ProjectArticle"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Account pages
const AccountLogin = lazy(() => import("./pages/account/AccountLogin"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const AccountDashboard = lazy(() => import("./pages/account/AccountDashboard"));
const AccountOrders = lazy(() => import("./pages/account/AccountOrders"));
const AccountOrderDetail = lazy(() => import("./pages/account/AccountOrderDetail"));
const AccountAddresses = lazy(() => import("./pages/account/AccountAddresses"));
const AccountProfile = lazy(() => import("./pages/account/AccountProfile"));
const ProtectedRoute = lazy(() => import("./components/account/ProtectedRoute"));

const queryClient = new QueryClient();

// Cart sync component
const CartSyncProvider = ({ children }: { children: React.ReactNode }) => {
  useCartSync();
  return <>{children}</>;
};

// Minimal loading fallback to prevent layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <ShopifyCustomerProvider>
          <CartSyncProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/product/:handle" element={<ProductDetail />} />
                <Route path="/collections/:slug" element={<Collection />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:handle" element={<ProjectArticle />} />
                <Route path="/sign-up" element={<Contact />} />
                {/* Account routes */}
                <Route path="/account/login" element={<AccountLogin />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/account" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><AccountDashboard /></ProtectedRoute></Suspense>} />
                <Route path="/account/orders" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><AccountOrders /></ProtectedRoute></Suspense>} />
                <Route path="/account/orders/:id" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><AccountOrderDetail /></ProtectedRoute></Suspense>} />
                <Route path="/account/addresses" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><AccountAddresses /></ProtectedRoute></Suspense>} />
                <Route path="/account/profile" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><AccountProfile /></ProtectedRoute></Suspense>} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </CartSyncProvider>
        </ShopifyCustomerProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
